var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, observable } from 'mobx';
import { pureStructureFrom } from '@/common/helpers/pureStructureFrom';
import useStore from '@/common/hooks/useStore';
import Notifier from '@/common/services/Notifier';
import Diamond from '@/product/entity/Diamond';
import SecurityAPI from '@/security/api/SecurityAPI';
import { AccessGroup } from '@/security/entities/AccessGroup';
import { AssigneeType } from '@/security/entities/AssigneeType';
import { Contact } from '@/security/entities/Contact';
import { accessFormDataToRequests, DEFAULT_ACCESS_FORM_DATA } from '@/security/types/AccessFormData';
import { fromAccessRequests } from '@/security/types/PrivateAccessProfile';
import { AccessEntry } from '../../entities/AccessEntry';
import { AccessRequest } from '../../entities/AccessRequest';
class PrivateAccessModalStore {
    targetEntities = [];
    targetType;
    defaultAssigneeOptions = [];
    initialAccessRequests = [];
    accessRequests = [];
    get accessProfiles() {
        return fromAccessRequests(this.accessRequests);
    }
    get canChangeCutDataAccess() {
        const entity = this.targetEntities?.[0];
        return this.targetType === 'project' || entity instanceof Diamond;
    }
    get addedEmails() {
        const newEmails = this.forms
            .flatMap((form) => form.assignees)
            .filter((assignee) => !(assignee instanceof AccessGroup))
            .map((assignee) => assignee.email.toLowerCase().trim());
        return new Set([
            ...newEmails,
            ...this.accessRequests.filter((req) => req.contact?.email).map((req) => req.contact.email.toLowerCase().trim()),
        ]);
    }
    get addedGroupIds() {
        const newGroupIds = this.forms
            .flatMap((form) => form.assignees)
            .filter((assignee) => assignee instanceof AccessGroup)
            .map((assignee) => assignee.id);
        return new Set([
            ...newGroupIds,
            ...this.accessRequests.filter((req) => req.accessGroup?.id).map((req) => req.accessGroup.id),
        ]);
    }
    isFetching = false;
    isUpdating = false;
    forms = [];
    securityAPI = new SecurityAPI();
    constructor(targetResources, type) {
        this.targetType = type;
        this.targetEntities = targetResources;
    }
    refresh = async () => {
        this.isFetching = true;
        this.forms = [];
        try {
            const [assigneesRes, accessEntryRes] = await Promise.all([
                this.securityAPI.getAssignees(),
                this.securityAPI.getAccessEntries(this.targetEntities.map((d) => d.id), this.targetType),
            ]);
            this.defaultAssigneeOptions = assigneesRes.assignees.map((d) => {
                return d.type === AssigneeType.Contact ? new Contact(d) : new AccessGroup(d);
            });
            const accessEntries = accessEntryRes.map((d) => new AccessEntry(d));
            this.initialAccessRequests = accessEntries.map((entry) => AccessRequest.fromEntry(entry));
            this.accessRequests = accessEntries.map((entry) => AccessRequest.fromEntry(entry));
        }
        catch (e) {
            Notifier.error(e);
        }
        finally {
            this.isFetching = false;
        }
    };
    loadAssignees = async (searchQuery) => {
        try {
            const { assignees } = await this.securityAPI.getAssignees(searchQuery);
            return assignees.map((d) => {
                return d.type === AssigneeType.Contact ? new Contact(d) : new AccessGroup(d);
            });
        }
        catch (e) {
            Notifier.error(e);
            return [];
        }
    };
    addAssignees = (assignees, dataAccess, recursive, message) => {
        const newRequests = assignees.flatMap((assignee) => this.targetEntities.map((target) => {
            return AccessRequest.createForAssignee(assignee, dataAccess, recursive, message, target.id.toString(), this.targetType);
        }));
        this.accessRequests = [...this.accessRequests, ...newRequests];
    };
    changeAssigneeAccess = (assigneeId, dataAccess) => {
        this.accessRequests = this.accessRequests.map((request) => {
            if (request.getAssigneeId() === assigneeId) {
                return new AccessRequest({ ...request, dataAccess });
            }
            return request;
        });
    };
    unselectAssignee = (assigneeId) => {
        this.accessRequests = this.accessRequests.filter((request) => {
            return request.getAssigneeId() !== assigneeId;
        });
    };
    updateACl = async () => {
        this.isUpdating = true;
        const { removedAccessReq, updateRequests } = this.getAccessRequestChanges();
        if (removedAccessReq.length == 0 && updateRequests.length === 0) {
            return [];
        }
        try {
            const promises = [];
            if (removedAccessReq.length > 0) {
                promises.push(this.securityAPI.deleteACL(removedAccessReq));
            }
            if (updateRequests.length > 0) {
                promises.push(this.securityAPI.updateACL(updateRequests));
            }
            const response = await Promise.all(promises);
            const result = response.flat();
            if (result.every(({ state }) => state === 'ok')) {
                Notifier.success('Updated successfully');
            }
            else if (result.every(({ state }) => state === 'error')) {
                Notifier.error('Fail to update');
            }
            else {
                Notifier.warn('Partially updated');
            }
            this.initialAccessRequests = [...this.accessRequests];
            const accessEntryRes = await this.securityAPI.getAccessEntries(this.targetEntities.map((d) => d.id), this.targetType);
            return await this.patchTargets(accessEntryRes.map((d) => new AccessEntry(d)));
        }
        catch (e) {
            Notifier.error(e);
            return [];
        }
        finally {
            this.isUpdating = false;
        }
    };
    addForm = () => {
        const newForm = pureStructureFrom(DEFAULT_ACCESS_FORM_DATA);
        this.forms.unshift(newForm);
    };
    removeForm = (index) => {
        this.forms.splice(index, 1);
    };
    updateForm = (index, field, value) => {
        if (this.forms[index]) {
            this.forms[index] = { ...this.forms[index], [field]: value };
        }
    };
    getAccessRequestChanges() {
        const newRequests = this.targetEntities.flatMap((target) => {
            return this.forms.flatMap((form) => {
                return accessFormDataToRequests(form, target.id.toString(), this.targetType, this.canChangeCutDataAccess);
            });
        });
        const requests = [...this.accessRequests, ...newRequests];
        const initialReqMap = new Map(this.initialAccessRequests.map((r) => [`${r.targetId}-${r.getAssigneeId()}`, r]));
        const currentReqMap = new Map(requests.map((r) => [`${r.targetId}-${r.getAssigneeId()}`, r]));
        const removedAccessReq = [...initialReqMap.entries()]
            .filter(([id]) => !currentReqMap.has(id))
            .map(([, req]) => req);
        const updateRequests = [...currentReqMap.entries()]
            .filter(([id, req]) => !req.isEqual(initialReqMap.get(id)))
            .map(([, req]) => req);
        return { removedAccessReq: removedAccessReq, updateRequests: updateRequests };
    }
    async patchTargets(accessEntries) {
        const assigneesById = accessEntries.reduce((map, entry) => {
            const existing = map.get(entry.getTargetId()) ?? { contactInfos: [], groupInfos: [] };
            if (entry.contact) {
                existing.contactInfos.push({ assignee: entry.contact, dataAccess: entry.dataAccess });
            }
            else if (entry.accessGroup) {
                existing.groupInfos.push({ accessGroup: entry.accessGroup, dataAccess: entry.dataAccess });
            }
            map.set(entry.getTargetId(), existing);
            return map;
        }, new Map());
        return this.targetEntities.map((entity) => {
            const assignees = assigneesById.get(entity.id);
            entity.acl = assignees?.contactInfos ?? [];
            entity.accessGroupAcl = assignees?.groupInfos ?? [];
            return entity;
        });
    }
}
__decorate([
    observable
], PrivateAccessModalStore.prototype, "targetEntities", void 0);
__decorate([
    observable
], PrivateAccessModalStore.prototype, "targetType", void 0);
__decorate([
    observable
], PrivateAccessModalStore.prototype, "defaultAssigneeOptions", void 0);
__decorate([
    observable
], PrivateAccessModalStore.prototype, "initialAccessRequests", void 0);
__decorate([
    observable
], PrivateAccessModalStore.prototype, "accessRequests", void 0);
__decorate([
    computed
], PrivateAccessModalStore.prototype, "accessProfiles", null);
__decorate([
    computed
], PrivateAccessModalStore.prototype, "canChangeCutDataAccess", null);
__decorate([
    computed
], PrivateAccessModalStore.prototype, "addedEmails", null);
__decorate([
    computed
], PrivateAccessModalStore.prototype, "addedGroupIds", null);
__decorate([
    observable
], PrivateAccessModalStore.prototype, "isFetching", void 0);
__decorate([
    observable
], PrivateAccessModalStore.prototype, "isUpdating", void 0);
__decorate([
    observable
], PrivateAccessModalStore.prototype, "forms", void 0);
__decorate([
    action
], PrivateAccessModalStore.prototype, "addAssignees", void 0);
__decorate([
    action
], PrivateAccessModalStore.prototype, "changeAssigneeAccess", void 0);
__decorate([
    action
], PrivateAccessModalStore.prototype, "unselectAssignee", void 0);
__decorate([
    action
], PrivateAccessModalStore.prototype, "updateACl", void 0);
__decorate([
    action
], PrivateAccessModalStore.prototype, "addForm", void 0);
__decorate([
    action
], PrivateAccessModalStore.prototype, "removeForm", void 0);
__decorate([
    action
], PrivateAccessModalStore.prototype, "updateForm", void 0);
export const usePrivateAccessModalStore = () => useStore(PrivateAccessModalStore);
export default PrivateAccessModalStore;
